import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {clamp} from '../../utils/helpers/clamp'
import {AddImage} from '../../models/redux/addImage'
import {DeleteImage} from '../../models/redux/deleteImage'
import {MirrorImage} from '../../models/redux/mirrorImage'
import {MoveImage} from '../../models/redux/moveImage'
import {Reorder} from '../../models/redux/reorder'
import {SelectImage} from '../../models/redux/selectImage'
import {SetAttribute} from '../../models/redux/setAttribute'
import {SetBrightness} from '../../models/redux/setBrightness'
import {SetInert} from '../../models/redux/setInert'
import {SetRotation} from '../../models/redux/setRotation'
import {SetScale} from '../../models/redux/setScale'
import {EditorStore, initialEditorStore} from './editor.store'
import {SetZoom} from '../../models/redux/setZoom'
import {SetInteractedImage} from '../../models/redux/setInteractedImage'
import {SetPrintArea} from '../../models/redux/setPrintArea'
import {PersistPosition} from '../../models/redux/persistPosition'
import {AddImageHistoryItem} from '../../models/redux/addImageHistoryItem'
import {UpdateImageHistoryPosition} from '../../models/redux/updateImageHistoryPosition'
import {Config} from '../../constants/Config'
import {PersistScale} from '../../models/redux/persistScale'
import {PersistRotation} from '../../models/redux/persistRotation'
import {SetPosition} from '../../models/redux/setPosition'
import {createPositionFromOffset} from '../../utils/helpers/imageHelper'
import {PersistBrightness} from '../../models/redux/persistBrightness'
import {ChangeColor} from '../../models/redux/changeColor'
import {FlipSide} from '../../models/redux/flipSide'
import {origo} from '../../utils/helpers/origo'
import {v4 as uuidv4} from 'uuid'

const editorSlice = createSlice({
  name: 'editor',
  initialState: initialEditorStore,
  reducers: {
    resetEditorStore(state) {
      state.imageHistoryPosition = initialEditorStore.imageHistoryPosition
      state.imageHistory = initialEditorStore.imageHistory
      state.images = initialEditorStore.images
      state.interactedImage = initialEditorStore.interactedImage
      state.printArea = initialEditorStore.printArea
      state.selectedImageId = initialEditorStore.selectedImageId
      state.zoom = initialEditorStore.zoom
    },
    loadEditorStore(state, action: PayloadAction<EditorStore>) {
      if (action.payload.imageHistoryPosition && action.payload.imageHistory) {
        state.imageHistoryPosition = action.payload.imageHistoryPosition
        state.imageHistory = action.payload.imageHistory
      } else {
        state.imageHistoryPosition = initialEditorStore.imageHistoryPosition
        state.imageHistory = initialEditorStore.imageHistory
      }
      state.images = action.payload.images
      state.interactedImage = action.payload.interactedImage
      state.printArea = action.payload.printArea
      state.selectedImageId = action.payload.selectedImageId
      state.zoom = action.payload.zoom
    },
    setPrintArea(state, action: PayloadAction<SetPrintArea>) {
      state.printArea = action.payload.printArea
    },
    addImage(state, action: PayloadAction<AddImage>) {
      state.images = [...state.images, action.payload.image]
    },
    changeColor(state, action: PayloadAction<ChangeColor>) {
      state.images = state.images.map((image, i) =>
        i === 0
          ? {
              ...image,
              url: action.payload.url,
            }
          : image,
      )
      state.maskImage = action.payload.maskUrl
        ? {
            id: uuidv4(),
            type: 'url',
            url: action.payload.maskUrl,
            brightness: 1,
            filters: {inverse: false, monochrome: false, sepia: false},
            inert: true,
            locked: true,
            mirroredX: false,
            mirroredY: false,
            position: origo,
            rotation: 0,
            scale: 1,
          }
        : null
    },
    flipSide(state, action: PayloadAction<FlipSide>) {
      state.images = action.payload.images
      state.imageHistory = initialEditorStore.imageHistory
      state.imageHistoryPosition = initialEditorStore.imageHistoryPosition
      state.interactedImage = initialEditorStore.interactedImage
      state.selectedImageId = initialEditorStore.selectedImageId
      state.maskImage = action.payload.maskImage
    },
    addImageSelected(state, action: PayloadAction<AddImage>) {
      state.images = [...state.images, action.payload.image]
      state.selectedImageId = action.payload.image.id
    },
    deleteImage(state, action: PayloadAction<DeleteImage>) {
      state.images = state.images.filter(
        image => image.id !== action.payload.id,
      )
    },
    setInteractedImage(
      state,
      action: PayloadAction<SetInteractedImage | null>,
    ) {
      state.interactedImage = action.payload
        ? {
            interaction: action.payload.interaction,
            id: action.payload.id,
            innerImageIndex: action.payload.innerImageIndex,
            type: action.payload.type,
          }
        : null
    },
    moveImage(state, action: PayloadAction<MoveImage>) {
      state.images = state.images.map(oldImage =>
        oldImage.id ===
          (action.payload.id ??
            (state.interactedImage && state.interactedImage.id)) &&
        !oldImage.locked
          ? {
              ...oldImage,
              position: createPositionFromOffset(
                oldImage,
                action.payload.offset,
              ),
            }
          : oldImage,
      )
    },
    setPosition(state, action: PayloadAction<SetPosition>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {...oldImage, position: action.payload.position}
          : oldImage,
      )
    },
    setInert(state, action: PayloadAction<SetInert>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.exceptId && !oldImage.locked
          ? oldImage
          : {...oldImage, inert: action.payload.inert},
      )
    },
    setScale(state, action: PayloadAction<SetScale>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {
              ...oldImage,
              scale: clamp(
                action.payload.scale,
                Config.minScale,
                action.payload.caption ? Config.maxTextScale : Config.maxScale,
              ),
            }
          : oldImage,
      )
    },
    persistScale(state, action: PayloadAction<PersistScale>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {...oldImage, origScale: oldImage.scale}
          : oldImage,
      )
    },
    persistBrightness(state, action: PayloadAction<PersistBrightness>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {...oldImage, origBrightness: oldImage.brightness}
          : oldImage,
      )
    },
    persistRotation(state, action: PayloadAction<PersistRotation>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {...oldImage, origRotation: oldImage.rotation}
          : oldImage,
      )
    },
    persistPosition(state, action: PayloadAction<PersistPosition>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {...oldImage, origPosition: oldImage.position}
          : oldImage,
      )
    },
    setRotation(state, action: PayloadAction<SetRotation>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {
              ...oldImage,
              rotation: ((action.payload.rotation + 540) % 360) - 180,
            }
          : oldImage,
      )
    },
    setBrightness(state, action: PayloadAction<SetBrightness>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {
              ...oldImage,
              brightness: clamp(action.payload.brightness, 0, 2),
            }
          : oldImage,
      )
    },
    mirrorX(state, action: PayloadAction<MirrorImage>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {
              ...oldImage,
              mirroredX:
                undefined !== action.payload.mirrorX
                  ? action.payload.mirrorX
                  : !oldImage.mirroredX,
            }
          : oldImage,
      )
    },
    mirrorY(state, action: PayloadAction<MirrorImage>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {
              ...oldImage,
              mirroredY:
                undefined !== action.payload.mirrorY
                  ? action.payload.mirrorY
                  : !oldImage.mirroredY,
            }
          : oldImage,
      )
    },
    selectImage(state, action: PayloadAction<SelectImage>) {
      state.selectedImageId = action.payload.id
    },
    setAttribute(state, action: PayloadAction<SetAttribute>) {
      state.images = state.images.map(oldImage =>
        oldImage.id === action.payload.id && !oldImage.locked
          ? {...oldImage, ...action.payload.data, id: oldImage.id}
          : oldImage,
      )
    },
    sendBack(state, action: PayloadAction<Reorder>) {
      const index = state.images.findIndex(
        image => image.id === action.payload.id,
      )
      if (
        index !== -1 &&
        index !== 0 &&
        index !== 1 &&
        !state.images[index].locked
      ) {
        state.images = [
          ...state.images.slice(0, index - 1),
          state.images[index],
          state.images[index - 1],
          ...state.images.slice(index + 1),
        ]
      }
    },
    bringFront(state, action: PayloadAction<Reorder>) {
      const index = state.images.findIndex(
        image => image.id === action.payload.id,
      )
      if (
        index !== -1 &&
        index !== state.images.length - 1 &&
        !state.images[index].locked
      ) {
        state.images = [
          ...state.images.slice(0, index),
          state.images[index + 1],
          state.images[index],
          ...state.images.slice(index + 2),
        ]
      }
    },
    setZoom(state, action: PayloadAction<SetZoom>) {
      state.zoom = {...action.payload}
    },
    updateImageHistoryPosition(
      state,
      action: PayloadAction<UpdateImageHistoryPosition>,
    ) {
      state.imageHistoryPosition = action.payload.position
    },
    addImageHistoryItem(state, action: PayloadAction<AddImageHistoryItem>) {
      // when new item is added, have to reset the history items
      // so the chain of the history items wont be broken on prev and next actions
      if (null !== state.imageHistoryPosition) {
        state.imageHistory =
          0 === state.imageHistoryPosition
            ? []
            : [
                ...state.imageHistory
                  .reverse()
                  .slice(state.imageHistoryPosition * -1)
                  .reverse(),
              ]
      }

      state.imageHistory.push({...action.payload.item})
      state.imageHistoryPosition = null
    },
  },
})

export const {
  resetEditorStore,
  loadEditorStore,
  setPrintArea,
  addImage,
  changeColor,
  flipSide,
  addImageSelected,
  deleteImage,
  setInteractedImage,
  moveImage,
  setInert,
  selectImage,
  setScale,
  setRotation,
  setBrightness,
  mirrorX,
  mirrorY,
  setAttribute,
  sendBack,
  bringFront,
  setZoom,
  persistPosition,
  persistScale,
  persistBrightness,
  persistRotation,
  addImageHistoryItem,
  updateImageHistoryPosition,
  setPosition,
} = editorSlice.actions
export default editorSlice.reducer
